import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {JumpingWild} from "../component/JumpingWild";
import {
    CAR_NAMES,
    DIRECTION,
    JumpingWildAnimStates,
    JumpinWildSkins,
    JW_TYPE,
    RoadKillSymbolStates,
    SYMBOL_NAME
} from "../component/enums/RoadKillEnums";
import {RoadKill} from "../RoadKill";
import {RoadKillUtils} from "../utils/RoadKillUtils";
import {RKHelper} from "../utils/RKHelper";
import {ISpecialSymData, ITrappedCarInfo, RoadKillGameData} from "../server/RoadKillData";
import {Back, gsap, TimelineLite} from "gsap";
import {ICarAnimalData} from "../gameflow/RESPIN/RoadKillRespinResultPresentation";
import {DisplayObject} from "pixi.js";
import {Multiplier} from "../component/Multiplier";
import {RoadKillGameAssets} from "../RoadKillGameAssets";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {Split} from "../component/Split";
import {RoadKillGameMode} from "../server/RoadKillGameMode";
import {RoadKillSound} from "../sound/RoadKillSound";
import {RoadKillSymbol} from "../reels/RoadKillSymbol";
import {RoadKillGameConfig} from "../RoadKillGameConfig";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";

export class JumpingWildView extends PIXI.Container implements StageLayer {
    private _sw1: JumpingWild;
    private _sw2: JumpingWild;
    private _sw3: JumpingWild;
    private _landedJW: JumpingWild[] = [];
    private _splitContainer: PIXI.Container;
    private _wildContainer: PIXI.Container;
    private _trapContainer: PIXI.Container;
    private _trapsMap: Map<string, PIXI.spine.Spine> = new Map<string, PIXI.spine.Spine>();
    private _greenTrappedCar: PIXI.Sprite | null;
    private _redTrappedCar: PIXI.Sprite | null;
    private _trappedCarsPosArray: ITrappedCarInfo[] = [];
    private _symbolMask: PIXI.Graphics;

    constructor() {
        super();
        this.name = "JumpingWildView";
        this.init();
        this.position.set(-1, -7);
    }

    private _reelSymbolContainer: PIXI.Container;

    get reelSymbolContainer(): PIXI.Container {
        return this._reelSymbolContainer;
    }

    get trappedCarPosition(): ITrappedCarInfo[] {
        return this._trappedCarsPosArray;
    }

    public getTrappedCar(carName: CAR_NAMES): PIXI.Sprite | null {
        return carName === CAR_NAMES.GREEN ? this._greenTrappedCar : this._redTrappedCar;
    }

    public onLand(specialSymData: ISpecialSymData, isRestore: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let currentAnimal: JumpingWild | undefined = this.getJWByAliasName(specialSymData.symName as SYMBOL_NAME);
        if (currentAnimal) {
            this._landedJW.push(currentAnimal);
            currentAnimal.data = specialSymData;
            if (isRestore && specialSymData.upgraded) {
                currentAnimal.onUpgrade();
            }
            tl.add(currentAnimal?.onLand(specialSymData.coordFrom[0], specialSymData.coordFrom[1], isRestore, specialSymData.upgraded));
        }
        return tl;
    }

    public degradeToWooden(symName: SYMBOL_NAME): void {
        if (symName === SYMBOL_NAME.SW1) {
            this._sw1.state.setAnimation(0, "idle", false);
        } else if (symName === SYMBOL_NAME.SW2) {
            this._sw2.state.setAnimation(0, "idle", false);
        } else {
            this._sw3.state.setAnimation(0, "idle", false);
        }
    }

    public onHit(carData: ICarAnimalData): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            hitFromLeft: boolean = carData.direction === DIRECTION.LEFT,
            gameData: RoadKillGameData = RoadKill.model.gameData;
        if (carData.animalData) {
            const isFinalHit: boolean = carData.hpLeft === 0;
            const type: JW_TYPE = RoadKillUtils.getJWType(carData.animalData.symName);
            if (type === JW_TYPE.BEAR) {
                tl.add(this._sw1.playHitImpact(hitFromLeft), 0);
                if (!carData.animalData.upgraded) {
                    const hitsFromThisSpin: number = this._sw1.hitCount;
                    if (RoadKill.backgroundElements.getMultiplierValue() + hitsFromThisSpin <= 5) {
                        tl.add([this.playAwardMultiplier(carData.destReelId, carData.rowId), () => {
                            RoadKill.soundPlayer.playTrack(RoadKillSound.MULTIPLIER_MOVE_SOUND);
                        }], 0);
                        tl.add(RoadKill.backgroundElements.addMultiplier(1));
                    }
                }
                tl.add(this._sw1.onHit(isFinalHit));

                if (this.isMassiveWildRound()) {
                    tl.add(RoadKill.massiveWinView.playSpecificAnimation("b_hit", false, true), 0);
                    if (RoadKill.model.bothCarHitDeerBearMassiveWild("Bear")) {
                        if (carData.name === "Green") {
                            tl.add(RoadKill.massiveWinView.showBearHitMutiplier(2));
                        } else {
                            tl.add(RoadKill.massiveWinView.showBearHitMutiplier4());
                        }
                    } else {
                        tl.add(RoadKill.massiveWinView.showBearHitMutiplier(2));
                    }
                }
            } else if (type === JW_TYPE.DEER) {
                tl.add(this._sw2.playHitImpact(hitFromLeft), 0);
                tl.add([
                    this.playSplitFeature(carData),
                    () => {
                        RoadKill.soundPlayer.playTrack(RoadKillSound.SPLIT_SYM);
                    }], 0);
                tl.add(this._sw2.onHit(isFinalHit));
                if (this.isMassiveWildRound()) {
                    tl.add(RoadKill.massiveWinView.playSpecificAnimation("d_hit", false, true), 0);
                }
                if (this.isMassiveWildRound() && RoadKill.model.bothCarHitDeerBearMassiveWild("Deer") && carData.name === "Red") {
                    tl.add(RoadKill.massiveWinView.showDeerHitMultipliers(), 0);
                }
            } else if (type === JW_TYPE.RABBIT) {
                tl.add([this.playExtraJump(carData),
                    this._sw3.playHitImpact(hitFromLeft)]);
                //tl.add(this._sw3.playHitImpact(hitFromLeft));

                if (carData.hpLeft === 0) {
                    this.createWildOverlay(carData.destReelId, carData.rowId);
                    tl.add(this.playWildOverlayInAnim(carData.destReelId, carData.rowId), 0);
                    tl.add(() => {
                        this.showTrappedCar(carData.name === "Green", carData.destReelId, carData.rowId);
                    }, "<");
                }
                if (RoadKill.model.gameData.doMassiveWildThisSpin) {
                    const rowIdxForWild: number = carData.name === "Green" ? RoadKill.model.gameData.evaluatedArea[4].indexOf("W") : RoadKill.model.gameData.evaluatedArea[4].lastIndexOf("W");
                    this.createWildOverlay(4, rowIdxForWild);
                    tl.add(this.playWildOverlayInAnim(4, rowIdxForWild), 0);
                    tl.add(() => {
                        this.showTrappedCar(carData.name === "Green", 4, rowIdxForWild);
                    }, "<");
                }
                tl.add(this._sw3.onHit(isFinalHit));

                if (gameData.evaluatedArea[carData.destReelId][carData.rowId] === SYMBOL_NAME.SW3 && gameData.symbolSizes[carData.destReelId][carData.rowId] === 2) {
                    tl.add(this._sw3.showX2Split(true));
                }
                if (this.isMassiveWildRound()) {
                    tl.add(RoadKill.massiveWinView.playSpecificAnimation("r_hit", false, true), 0);
                }
                //tl.add(this.playExtraJump(carData));
            } else if (type === JW_TYPE.TRAP) {
                if (isFinalHit) {//hitCount adjusted at updateTopAnimalHits if 2 hit in 1 spin
                    tl.add(RoadKill.topAnimalController.updateTopAnimalHits(carData.animalData.symName as SYMBOL_NAME, RoadKill.model.mode === RoadKillGameMode.SUPER_FREESPIN ? RoadKill.model.totalAnimalHitsSFS : carData.animalData.getHitCount));
                }
                tl.add([this.onTrapHit(carData.animalData.rabbitTrap[0], carData.animalData.rabbitTrap[1], carData.hpLeft === 0, carData.name === "Green"), () => {
                    RoadKill.soundPlayer.playTrack(RoadKillSound.TINKER_HIT);
                }], 0);
            }

        }
        return tl;
    }

    public onSpinStart(): TimelineLite {
        return new TimelineLite().add(() => {
            this.fadeOutTraps();
            this.fadeOutSplits();
            this.fadeOutWildOverlay();
            if (RoadKill.model.initData.isRestoreState && !RoadKill.model.gameData && RoadKill.model.initData.totalAnimalHitsSFS === 20) {
                this.hideShowAllSW(0);
            } else {
                this.fadeInJW();
            }
            this._sw1.removeMultiplier();
            this._sw2.removeMultiplier();
            this._sw3.removeMultiplier();
            if (this._sw1.splitStateVisible) {
                this._sw1.showX2Split(false);
            }
            if (this._sw2.splitStateVisible) {
                this._sw2.showX2Split(false);
            }
            if (this._sw3.splitStateVisible) {
                this._sw3.showX2Split(false);
            }
        });
    }

    public reset(): TimelineLite {
        return new TimelineLite().add(() => {
            this._sw1.hide();
            this._sw2.hide();
            this._sw3.hide();
            this._landedJW.length = 0;
        });
    }

    public hideShowAllSW(alpha: number = 0): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        for (let swIdx: number = 0; swIdx < this.getWilds().length; swIdx++) {
            tl.to(this.getWilds()[swIdx], {alpha: alpha, duration: 0.0001}, 0);
        }
        return tl;
    }

    public onResult(specSymData: ISpecialSymData, calledForMassiveWild: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (specSymData.coordTo && specSymData.coordTo.length) {
            let reelId: number = specSymData.coordTo[0];
            let rowId: number = specSymData.coordTo[1];
            let currentAnimal: JumpingWild;
            const type: JW_TYPE = RoadKillUtils.getJWType(specSymData.symName);
            if (type === JW_TYPE.BEAR) {
                currentAnimal = this._sw1;
            } else if (type === JW_TYPE.DEER) {
                currentAnimal = this._sw2;
            } else if (type === JW_TYPE.RABBIT) {
                currentAnimal = this._sw3;
                currentAnimal.jumpIndex = 0;
                if (specSymData.coordToRabbitJumps[0]) {
                    reelId = specSymData.coordToRabbitJumps[0][0];
                    rowId = specSymData.coordToRabbitJumps[0][1];
                }
            }
            if (currentAnimal!) {
                currentAnimal.data = specSymData;
                if (currentAnimal.visible) {
                    if (specSymData.rabbitTrap && currentAnimal.upgraded && type === JW_TYPE.RABBIT && !calledForMassiveWild) {
                        tl.add([this.addTrap(specSymData.rabbitTrap[0], specSymData.rabbitTrap[1]), () => {
                            RoadKill.soundPlayer.playTrack(RoadKillSound.TINKER);
                        }], 0);
                    }
                    tl.add(currentAnimal.jumpToPos({
                        reelId: reelId,
                        rowId: rowId,
                    }), 0);
                    tl.add(RKHelper.playSpineWithTimeline(currentAnimal, 0, JumpingWildAnimStates.JUMP).timeScale(1.5), 0);
                    tl.add(() => {
                        currentAnimal.autoUpdate = true;
                        currentAnimal.state.setAnimation(0, JumpingWildAnimStates.IDLE, true);
                        currentAnimal.animationState = JumpingWildAnimStates.IDLE;
                    }, ">")
                }
            }
        }
        return tl;
    }

    public getWilds(): JumpingWild[] {
        return [this._sw1, this._sw2, this._sw3];
    }

    public playExtraJump(carData: ICarAnimalData): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const specSymData: ISpecialSymData = carData.animalData;
        this._sw3.jumpIndex++;
        let currentIndex: number = this._sw3.jumpIndex;
        if (currentIndex > 0 && specSymData.coordToRabbitJumps[currentIndex]) {
            const reelId: number = specSymData.coordToRabbitJumps[currentIndex][0];
            const rowId: number = specSymData.coordToRabbitJumps[currentIndex][1];
            const tls: TimelineSprite = new TimelineSprite(RoadKill.assetLoader.getTextures("rabbitSmoke"));
            tls.alpha = 0;
            tls.anchor.set(0.5);
            tls.show();
            this.addChild(tls);
            tl.add(() => {
                tls.position.set(this._sw3.position.x, this._sw3.position.y);
            });
            tl.to(tls, {duration: 0.01, alpha: 1}, 0);
            tl.add([tls.getAnimationAutoShowHide(true, true), () => {
                RoadKill.soundPlayer.playTrack(RoadKillSound.RABBIT_EXPLOSION);
            }], 0);
            tl.add(this.getExtraJumpAnim(reelId, rowId), 0);
            tl.add(() => {
                tls.hide();
                tls.parent?.removeChild(tls);
            })
        }
        return tl;
    }

    public onReelStop(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const symSize: number[][] = RoadKill.model.data.symbolSizes;

        RoadKill.model.characterData.forEach((specSymData: ISpecialSymData, jwType: JW_TYPE) => {
            if (specSymData.coordTo) {
                const reelId: number = specSymData.coordTo[0];
                const rowId: number = specSymData.coordTo[1];
                if (symSize[reelId][rowId] >= 2) {
                    if (specSymData.symName === SYMBOL_NAME.SW1 && symSize[reelId][rowId] === 4) {
                        tl.add(this.getWilds()[0].showX2Split(true), 0);
                    } else if (specSymData.symName === SYMBOL_NAME.SW3) {
                        tl.add(this.getWilds()[2].showX2Split(true), 0);
                    }
                }
            }
        });
        tl.add(this.fadeOutTraps());
        return tl;
    }

    public getSplits(): Split[] {
        return this._splitContainer.children as Split[];
    }

    public onModeChange(nextMode: RoadKillGameMode): void {
        this._sw1.onModeChange(nextMode);
        this._sw2.onModeChange(nextMode);
        this._sw3.onModeChange(nextMode);
        RoadKill.topAnimalController.onModeChange(nextMode);
        RoadKill.lifeView.update(RoadKill.model.data.heartList);
        RoadKill.backgroundElements.updateMultiplier(RoadKill.model.data.persistentMultiplier);
        if (nextMode === RoadKillGameMode.SUPER_FREESPIN) {
            RoadKill.backgroundElements.playPersistentMultiplerSound();
        }
    }

    public getJWByAliasName(aliasName: SYMBOL_NAME): JumpingWild | undefined {
        let jw: JumpingWild | undefined;
        if (aliasName === SYMBOL_NAME.SW1) {
            jw = this._sw1;
        } else if (aliasName === SYMBOL_NAME.SW2) {
            jw = this._sw2;
        } else if (aliasName === SYMBOL_NAME.SW3) {
            jw = this._sw3;
        }
        return jw;
    }

    public getOverlayWildContainer(): PIXI.Container {
        return this._wildContainer;
    }

    public getOverlaySplitContainer(): PIXI.Container {
        return this._splitContainer;
    }

    public fadeOutTraps(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._trapsMap.forEach((obj: PIXI.spine.Spine, key: string) => {
            tl.to(obj.scale, {duration: 0.2, x: 0, y: 0}, 0);
            tl.to(obj, {
                duration: 0.2,
                alpha: 0,
                onComplete: () => {
                    obj.visible = false;
                    obj.parent?.removeChild(obj);
                    this._trapsMap.delete(key);
                }
            }, 0)
        });
        return tl;
    }

    public fadeOutTrappedCars(): TimelineLite {
        const tl: TimelineLite = new TimelineLite({
            onComplete: () => {
                this._redTrappedCar?.parent.removeChild(this._redTrappedCar);
                this._greenTrappedCar?.parent.removeChild(this._greenTrappedCar);
                this._redTrappedCar = null;
                this._greenTrappedCar = null;
                for (let trappedCarIdx: number = 0; trappedCarIdx < RoadKill.jumpingWildView.trappedCarPosition.length; trappedCarIdx++) {
                    delete RoadKill.jumpingWildView.trappedCarPosition[trappedCarIdx];
                }
                this.trappedCarPosition.length = 0;
            }
        });
        if (this._greenTrappedCar) {

            tl.to(this._greenTrappedCar, {alpha: 0, duration: 0.2});
        }
        if (this._redTrappedCar) {
            tl.to(this._redTrappedCar, {alpha: 0, duration: 0.2});
        }
        return tl;
    }

    public playAwardMultiplier(reelId: number, rowId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const multiplier: Multiplier = new Multiplier(RoadKillGameAssets.BITMAP_FONT_NUMBERS_MULTIPLIER, 44, "x");
        multiplier.scale.set(0);
        multiplier.position = RoadKillUtils.getSymAbsolutePos(reelId, rowId);
        this.addChild(multiplier);
        const destPos: PIXI.Point = NolimitApplication.isLandscape ? new PIXI.Point(636, 76) : new PIXI.Point(636, 76);
        tl.add(() => {
            multiplier.showIfDefaultValue = true;
            multiplier.value = 1;
        });
        tl.to(multiplier, 0.001, {alpha: 1});
        tl.to(multiplier.scale, 0.3, {x: 1, y: 1, ease: Back.easeOut.config(1.7)});
        tl.to(multiplier, 0.3, {
            x: destPos.x, y: destPos.y, ease: Back.easeIn.config(1.7)
        }, 0.6);
        tl.to(multiplier, 0.4, {alpha: 0.2}, 0.9);
        tl.to(multiplier.scale, 0.2, {x: 0.1, y: 0.1}, 0.9);
        tl.add(() => {
            multiplier.parent?.removeChild(multiplier);
        });
        return tl;
    }

    public onReelStopRemoveMask(): void {
        if (this._reelSymbolContainer.mask) {
            this._reelSymbolContainer.mask = null;
            this._symbolMask.visible = false;
        }
    }

    public onReelStartAddMask(): void {
        if (!this._reelSymbolContainer.mask && this._symbolMask && this._reelSymbolContainer.children.length > 1) {
            this._reelSymbolContainer.mask = this._symbolMask;
            this._symbolMask.visible = true;
        }
    }

    private isMassiveWildRound(): boolean {
        return RoadKill.model.gameData && RoadKill.model.gameData.doMassiveWildThisSpin;
    }

    private addSplitMul(symName: SYMBOL_NAME, reelId: number, rowId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const jw: JumpingWild | undefined = this.getJWByAliasName(symName);
        if (jw) {
            jw.addMultiplier(2);
            jw.multiplier.scale.set(0);
            tl.to(jw.multiplier.scale, {duration: 0.2, x: 1, y: 1});
            this._splitContainer.children.forEach((obj: DisplayObject) => {
                if ((obj as Split).reelId === reelId && (obj as Split).rowId === rowId) {
                    tl.add((obj as Split).playOut(), 0)
                }
            });
        }
        return tl;
    }

    private onTrapHit(reelId: number, rowId: number, isCarDown: boolean, isGreenCar: boolean): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const trap: PIXI.spine.Spine | undefined = this._trapsMap.get(reelId + "_" + rowId);

        if (trap) {
            if (isCarDown) {
                trap.position.y += 8;
                trap.position.x += 2;
                this.createWildOverlay(reelId, rowId);
                tl.add([
                    RKHelper.playSpineWithTimeline(trap, 0, isGreenCar ? "SW3_trapCar1" : "SW3_trapCar2"),
                    this.playWildOverlayInAnim(reelId, rowId)]);
            }
            tl.to(trap, {duration: 0.1, alpha: 0});
            if (isCarDown) {
                tl.add(() => {
                    this.showTrappedCar(isGreenCar, reelId, rowId);
                }, "<");
            }
        }
        return tl;
    }

    private showTrappedCar(isGreenCar: boolean, reelId: number, rowId: number): void {
        const carTexture: string = isGreenCar ? "greenTrapCar" : "redTrapCar", //#218
            pos: PIXI.Point = RoadKillUtils.getSymAbsolutePos(reelId, rowId),
            trappedCar: PIXI.Sprite = new PIXI.Sprite(RoadKill.assetLoader.getTextures(carTexture)[0]);
        trappedCar.anchor.set(0.5);
        trappedCar.position.set(pos.x + 2, pos.y + 16);
        trappedCar.name = reelId + "_" + rowId;
        if (isGreenCar) {
            this._greenTrappedCar = trappedCar;
            this.storeCarPos(CAR_NAMES.GREEN, reelId, rowId);
            this.addChildAt(this._greenTrappedCar, 0);
        } else {
            this.storeCarPos(CAR_NAMES.RED, reelId, rowId);
            this._redTrappedCar = trappedCar;
            this.addChildAt(this._redTrappedCar, 0);
        }
    }

    private storeCarPos(carName: string, reelIdx: number, symIdx: number): void {
        for (let idx: number = 0; idx < this._trappedCarsPosArray.length; idx++) {
            if (this._trappedCarsPosArray[idx][carName]) {
                delete this._trappedCarsPosArray[idx][carName];
            }
        }
        this._trappedCarsPosArray.push({[carName]: [reelIdx, symIdx]});
    }

    private addTrap(reelId: number, rowId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const trap: PIXI.spine.Spine = new PIXI.spine.Spine(RoadKill.assetLoader.getSpineAsset("SW3"));
        this._trapContainer.addChild(trap);
        this._trapsMap.set(reelId + "_" + rowId, trap);
        trap.position = RoadKillUtils.getSymAbsolutePos(reelId, rowId);
        trap.position.y += 8;
        trap.alpha = 0;
        tl.add([
            gsap.to(trap, {duration: 0.1, alpha: 1}),
            RKHelper.playSpineWithTimeline(trap, 0, "SW3_trapIn")
        ])
        return tl;
    }

    private playSplitIn(reelId: number, rowId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (RoadKill.model.gameData.symbolSizes[reelId][rowId] >= 2) {
            const splitSpine: Split = new Split(reelId, rowId);
            this._splitContainer.addChild(splitSpine);
            tl.add(splitSpine.playIn());
        } else {
            console.warn("Please check server data mismatch from UI split issue at reel Id ", reelId, " and rowId ", rowId);
            debugger
        }
        return tl;
    }

    private fadeOutWildOverlay(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._wildContainer.children.forEach((obj: DisplayObject) => {
            tl.to(obj, {
                duration: 0.1, onComplete: () => {
                    obj.visible = false;
                }
            }, 0)
        });
        tl.add(() => {
            this._wildContainer.removeChildren();
        })
        return tl;
    }

    private fadeOutSplits(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._splitContainer.children.forEach((obj: DisplayObject) => {
            tl.add((obj as Split).playOut(), 0);
        });
        tl.add(() => {
            this._splitContainer.removeChildren();
        })
        return tl;
    }

    private fadeInJW(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._landedJW.forEach((jw: JumpingWild) => {
            if (jw.upgraded) {
                jw.setSkin(JumpinWildSkins.UPGRADED);
            } else {
                jw.setSkin(JumpinWildSkins.NORMAL);
            }
            jw.autoUpdate = true;
            if (jw.reelPosition) {
                const sym: RoadKillSymbol | undefined = RoadKillUtils.getSymbol(jw.reelPosition.reelId, jw.reelPosition.rowId);
                if (sym && RoadKillGameConfig.WILD_SYMBOLS.indexOf(sym.symbolName) > -1) {
                    const toSymbol: SYMBOL_NAME = RoadKill.model.data.reels[jw.reelPosition.reelId][jw.reelPosition.rowId + 1] as SYMBOL_NAME;
                    jw.autoUpdate = true;
                    if (sym.state === RoadKillSymbolStates.WIN) {
                        jw.state.setAnimation(0, JumpingWildAnimStates.WIN_STATE, false);
                        jw.animationState = JumpingWildAnimStates.WIN_STATE;
                    } else {
                        jw.state.setAnimation(0, JumpingWildAnimStates.IDLE, true);
                        jw.animationState = JumpingWildAnimStates.IDLE;
                    }
                    tl.to(sym, {duration: 0.1, alpha: 0}, 0.01);
                    tl.add(() => sym.changeSymbol(toSymbol), 0.1);
                    tl.to(sym, {duration: 0.1, alpha: 1});
                }
            }
            tl.to(jw, {duration: 0.001, alpha: 1}, 0.01);
        });
        return tl;
    }

    private getExtraJumpAnim(destReelId: number, destRowId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add(this._sw3.jumpToPos({
            reelId: destReelId,
            rowId: destRowId,
        }, true));
        return tl;
    }

    private createWildOverlay(reelId: number, rowId: number): void {
        const wildOverlay: PIXI.spine.Spine = new PIXI.spine.Spine(RoadKill.assetLoader.getSpineAsset("wildOverlay"));
        wildOverlay.name = `${reelId}${rowId}`;
        wildOverlay.visible = false;
        wildOverlay.alpha = 0;
        wildOverlay.position = RoadKillUtils.getSymAbsolutePos(reelId, rowId);
        wildOverlay.position.y += (-42);
        this._wildContainer.addChild(wildOverlay);
    }

    private playWildOverlayInAnim(reelId: number, rowId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        for (let childIdx: number = 0; childIdx < this._wildContainer.children.length; childIdx++) {
            if (this._wildContainer.getChildAt(childIdx).name === `${reelId}${rowId}`) {
                const respectiveSpine = this._wildContainer.getChildAt(childIdx);
                if (!respectiveSpine.visible) {
                    tl.add(() => {
                        respectiveSpine.alpha = 0;
                        respectiveSpine.visible = true;
                    });
                    tl.to(respectiveSpine, {alpha: 1, duration: 0.2});
                    tl.add(RKHelper.playSpineWithTimeline(respectiveSpine as PIXI.spine.Spine, 0, "W_In"), "<");
                }
            }
        }
        return tl;
    }

    private playSplitFeature(carData: ICarAnimalData): TimelineLite {
        this._splitContainer.removeChildren();
        const tl: TimelineLite = new TimelineLite();
        const startReelId: number = carData.direction === DIRECTION.LEFT ? carData.sourceReelId : carData.destReelId + 1;
        const endReelId: number = carData.direction === DIRECTION.LEFT ? carData.destReelId : carData.sourceReelId + 1;
        for (let reel: number = startReelId; reel < endReelId; reel++) {
            if (this._sw2.upgraded && carData.animalData && carData.animalData?.upgraded) {
                for (let row: number = 0; row < 3; row++) {
                    tl.add(this.playSplitIn(reel, row), reel * 0.1);
                }
            } else {
                tl.add(this.playSplitIn(reel, carData.rowId), reel * 0.1);
            }
        }
        return tl;
    }

    private init(): void {
        this._reelSymbolContainer = new PIXI.Container();
        this._reelSymbolContainer.name = "REEL_SYMBOL_CONTAINER";
        this._reelSymbolContainer.position.set(+3, 300 + 3);


        this._symbolMask = new PIXI.Graphics();
        this._symbolMask.beginFill();
        this._symbolMask.drawRect(-RoadKillGameConfig.SYMBOL_WIDTH * 0.5, -153, RoadKillGameConfig.SYMBOL_WIDTH * 6, RoadKillGameConfig.SYMBOL_HEIGHT * 3);
        this._reelSymbolContainer.addChild(this._symbolMask);
        this._reelSymbolContainer.mask = this._symbolMask;

        this._splitContainer = new PIXI.Container();
        this._splitContainer.name = "SPLIT_CONTAINER";

        this._wildContainer = new PIXI.Container();
        this._wildContainer.name = "WILD_CONTAINER";

        this._trapContainer = new PIXI.Container();
        this._trapContainer.name = "TRAP_CONTAINER";
        this.addChild(this._reelSymbolContainer, this._trapContainer, this._splitContainer, this._wildContainer);
        this._sw1 = new JumpingWild(RoadKill.assetLoader.getSpineAsset("SW1"), JW_TYPE.BEAR, SYMBOL_NAME.SW1);
        this._sw2 = new JumpingWild(RoadKill.assetLoader.getSpineAsset("SW2"), JW_TYPE.DEER, SYMBOL_NAME.SW2);
        this._sw3 = new JumpingWild(RoadKill.assetLoader.getSpineAsset("SW3"), JW_TYPE.RABBIT, SYMBOL_NAME.SW3);
        this.addChild(this._sw1, this._sw2, this._sw3);
    }
}